import {
  Loader2Icon,
  AlertTriangleIcon,
  FileTextIcon,
  ReceiptIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
} from 'lucide-react'
import { useState, useEffect } from 'react'

import {
  invoiceApi,
  type InvoiceSummary,
  type InvoiceDetail,
  type InvoiceListResponse,
} from '../services'
// A API embrulha toda HTTPException em {error:{message}} (main.py): ler
// `data.detail` devolvia sempre undefined e o texto do servidor nunca aparecia.
import { getErrorMessage } from '../utils'

export type InvoicesVariant = 'provider' | 'reseller-received' | 'reseller-issued'

type VariantConfig = {
  list: (params?: {
    skip?: number
    limit?: number
    recipient_id?: string
  }) => Promise<InvoiceListResponse>
  get: (id: string) => Promise<InvoiceDetail>
  counterpartField: 'issuer_name' | 'recipient_name'
  counterpartHeader: string
  empty: string
}

const VARIANTS: Record<InvoicesVariant, VariantConfig> = {
  provider: {
    // `recipient_id` não se aplica: aqui o destinatário é o reseller, não um cliente.
    list: (p) => invoiceApi.listProviderInvoices({ skip: p?.skip, limit: p?.limit }),
    get: (id) => invoiceApi.getProviderInvoice(id),
    counterpartField: 'recipient_name',
    counterpartHeader: 'Reseller',
    empty: 'As faturas emitidas aos seus resellers aparecem aqui no fechamento de cada mês.',
  },
  'reseller-received': {
    // idem: quem recebe é a própria org, então não há por quem filtrar.
    list: (p) =>
      invoiceApi.listResellerInvoices({ skip: p?.skip, limit: p?.limit, direction: 'received' }),
    get: (id) => invoiceApi.getResellerInvoice(id),
    counterpartField: 'issuer_name',
    counterpartHeader: 'Provedor',
    empty: 'As faturas que o seu provedor emite aparecem aqui no fechamento de cada mês.',
  },
  'reseller-issued': {
    list: (p) => invoiceApi.listResellerInvoices({ ...p, direction: 'issued' }),
    get: (id) => invoiceApi.getResellerInvoice(id),
    counterpartField: 'recipient_name',
    counterpartHeader: 'Cliente',
    empty: 'As faturas que você emitiu aos seus clientes aparecem aqui.',
  },
}

type InvoicesPanelProps = {
  variant: InvoicesVariant
  /**
   * Só em 'reseller-issued': restringe a lista às faturas de um cliente. É o que
   * o link "ver faturas" do contrato usa para não jogar o usuário numa lista de
   * todos os clientes.
   */
  recipientId?: string
  /** Chamado no "limpar filtro" do chip; sem ele o chip não aparece. */
  onClearRecipientFilter?: () => void
  /**
   * Fatura a abrir já no detalhe. É o destino do link "ver fatura" do contrato:
   * sem isso ele largaria o usuário na lista, com a fatura certa a procurar.
   */
  initialInvoiceId?: string
}

/**
 * Painel reutilizável de faturas (lista + detalhe). Usado por cp (emitidas) e
 * rs (recebidas/emitidas). A camada EC tem sua própria aba em BillingPage.
 */
export function InvoicesPanel({
  variant,
  recipientId,
  onClearRecipientFilter,
  initialInvoiceId,
}: InvoicesPanelProps) {
  const cfg = VARIANTS[variant]

  const [invoices, setInvoices] = useState<InvoiceSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<InvoiceDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)

  useEffect(() => {
    let cancelled = false
    setSelected(null)
    setLoading(true)
    setError(null)
    cfg
      .list({ limit: 50, recipient_id: recipientId })
      .then((r) => {
        if (!cancelled) setInvoices(r.invoices)
      })
      .catch((error: any) => {
        if (!cancelled) setError(getErrorMessage(error))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, recipientId])

  // Abre a fatura pedida pela URL uma vez, depois de a lista carregar.
  useEffect(() => {
    if (!initialInvoiceId) return
    let cancelled = false
    setLoadingDetail(true)
    cfg
      .get(initialInvoiceId)
      .then((inv) => {
        if (!cancelled) setSelected(inv)
      })
      .catch((error: any) => {
        if (!cancelled) setError(getErrorMessage(error))
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialInvoiceId, variant])

  const openDetail = async (id: string) => {
    setLoadingDetail(true)
    try {
      setSelected(await cfg.get(id))
    } catch (error: any) {
      setError(getErrorMessage(error))
    } finally {
      setLoadingDetail(false)
    }
  }

  if (loadingDetail) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2Icon className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Carregando fatura...</span>
      </div>
    )
  }

  // Detalhe
  if (selected) {
    const inv = selected
    const counterpart = (inv as any)[cfg.counterpartField] as string | null
    return (
      <div className="space-y-6">
        <button
          onClick={() => setSelected(null)}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="mr-1 h-4 w-4" /> Voltar para faturas
        </button>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900">
                  {invoiceApi.formatBillingPeriod(inv.billing_period)}
                </h2>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${invoiceApi.getInvoiceStatusColor(
                    inv.status,
                  )}`}
                >
                  {invoiceApi.getInvoiceStatusLabel(inv.status)}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Fatura {inv.invoice_number}
                {counterpart ? ` · ${cfg.counterpartHeader}: ${counterpart}` : ''}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold text-gray-900">
                {invoiceApi.formatCurrency(inv.total_amount, inv.currency)}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white shadow">
          <div className="border-b p-6">
            <h3 className="text-base font-semibold text-gray-900">Itens do período</h3>
          </div>

          {inv.line_items.length === 0 ? (
            <div className="p-12 text-center">
              <FileTextIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
              <p className="text-gray-600">Sem itens neste período.</p>
            </div>
          ) : (
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Descrição
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Valor
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {inv.line_items.map((li) => (
                    <tr key={li.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{li.description}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{li.resource_type || '—'}</td>
                      <td className="px-6 py-4 text-right text-sm font-semibold whitespace-nowrap text-gray-900">
                        {invoiceApi.formatCurrency(li.amount, li.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td
                      colSpan={2}
                      className="px-6 py-3 text-right text-sm font-medium text-gray-700"
                    >
                      Total
                    </td>
                    <td className="px-6 py-3 text-right text-sm font-bold whitespace-nowrap text-gray-900">
                      {invoiceApi.formatCurrency(inv.total_amount, inv.currency)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Mobile */}
          {inv.line_items.length > 0 && (
            <div className="divide-y divide-gray-200 md:hidden">
              {inv.line_items.map((li) => (
                <div key={li.id} className="space-y-1 p-4">
                  <div className="text-sm text-gray-800">{li.description}</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {invoiceApi.formatCurrency(li.amount, li.currency)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Lista
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2Icon className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Carregando faturas...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6">
        <div className="flex items-center space-x-3">
          <AlertTriangleIcon className="h-6 w-6 text-red-600" />
          <div>
            <h3 className="font-medium text-red-900">Erro ao carregar faturas</h3>
            <p className="mt-1 text-red-700">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  // Sem o chip, uma lista filtrada (ou vazia por causa do filtro) pareceria a
  // lista inteira — e "esse cliente não tem fatura" viraria "não há faturas".
  const filterChip =
    recipientId && onClearRecipientFilter ? (
      <div className="mb-3 flex items-center gap-2 text-sm">
        <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 font-medium text-emerald-700">
          Filtrado por cliente
        </span>
        <button
          onClick={onClearRecipientFilter}
          className="text-gray-500 underline hover:text-gray-700"
        >
          Limpar filtro
        </button>
      </div>
    ) : null

  if (invoices.length === 0) {
    return (
      <div>
        {filterChip}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-12 text-center">
          <ReceiptIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">Nenhuma fatura ainda</h3>
          <p className="text-gray-600">
            {recipientId ? 'Este cliente ainda não tem faturas emitidas.' : cfg.empty}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {filterChip}
      <div className="rounded-lg bg-white shadow">
        {/* Desktop */}
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Período
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Fatura
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {cfg.counterpartHeader}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Valor
                </th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  onClick={() => openDetail(inv.id)}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-gray-900">
                    {invoiceApi.formatBillingPeriod(inv.billing_period)}
                  </td>
                  <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                    {inv.invoice_number}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {(inv as any)[cfg.counterpartField] || '—'}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${invoiceApi.getInvoiceStatusColor(
                        inv.status,
                      )}`}
                    >
                      {invoiceApi.getInvoiceStatusLabel(inv.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-semibold whitespace-nowrap text-gray-900">
                    {invoiceApi.formatCurrency(inv.total_amount, inv.currency)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <ChevronRightIcon className="inline h-5 w-5 text-gray-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile */}
        <div className="divide-y divide-gray-200 md:hidden">
          {invoices.map((inv) => (
            <button
              key={inv.id}
              onClick={() => openDetail(inv.id)}
              className="flex w-full items-center justify-between p-4 text-left hover:bg-gray-50"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {invoiceApi.formatBillingPeriod(inv.billing_period)}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${invoiceApi.getInvoiceStatusColor(
                      inv.status,
                    )}`}
                  >
                    {invoiceApi.getInvoiceStatusLabel(inv.status)}
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  {(inv as any)[cfg.counterpartField] || inv.invoice_number}
                </div>
                <div className="text-sm font-semibold text-gray-900">
                  {invoiceApi.formatCurrency(inv.total_amount, inv.currency)}
                </div>
              </div>
              <ChevronRightIcon className="h-5 w-5 text-gray-400" />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default InvoicesPanel
